import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PageTitle } from 'components/layouts/pageTitle';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { updateRestrictedResources } from 'api/restaurant';

export const CompanyOnboardingForm = ({ itemData }) => {
  const { state: auth, onCompanyOnboardingCompleted } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);
  const navigate = useNavigate();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    name: yup.string().required('Please enter a company name.').default(itemData?.name),
    address: yup.string().required('Field required').default(itemData?.address),
    company_code: yup.string().required('Field required').default(itemData?.company_code),
    vat: yup.string().default(itemData?.vat),
    iban: yup.string().required('Field required').default(itemData?.iban),
    iban_repeated: yup.string().default(itemData?.iban).required('Field required').oneOf([yup.ref('iban'), null], "Bank account doesn't match"),
    email: yup.string().required('Field required').default(itemData?.email),
    phone: yup.string().required('Field required').default(itemData?.phone),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(itemData);
  }, [itemData, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    const response = await updateRestrictedResources({
      token: auth.token,
      updateKey: 'complete_onboarding',
      values: {
        name: data.name,
        address: data.address,
        company_code: data.company_code,
        vat: data.vat,
        iban: data.iban,
        email: data.email,
        phone: data.phone,
      },
      alertController,
      companyId: auth.company?.company,
    });

    if (response && response.isAcknowledged) {
      alertController.success({ text: 'Onboarding completed' });
      onCompanyOnboardingCompleted();
      navigate('/', { replace: true });
    }

    setIsLoading(false);
  };
  const onError = (data) => console.log(data);

  if (itemData && itemData.onboarding_completed) {
    return (
      <>
        <PageTitle
          title="Onboarding"
          isTopLevel
        />
        <div className="container mx-auto px-2 md:px-6 lg:px-8">Onboarding completed</div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        title="Onboarding"
        isTopLevel
      />
      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">

                <div className="grid grid-cols-6 gap-6">

                  <div className="col-span-6">
                    <p className="text-base font-medium text-gray-900">Company Details</p>
                  </div>

                  <TextboxWrapper
                    register={register}
                    name="name"
                    title="Company name*"
                    error={errors?.name}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('name')}
                  />

                  <TextboxWrapper
                    register={register}
                    name="address"
                    title="Company address*"
                    error={errors?.address}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('address')}
                  />

                  <TextboxWrapper
                    register={register}
                    name="company_code"
                    title="Company code*"
                    error={errors?.company_code}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('company_code')}
                  />

                  <TextboxWrapper
                    register={register}
                    name="vat"
                    title="VAT code"
                    error={errors?.vat}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('vat')}
                  />

                  <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />
                  <div className="col-span-6">
                    <p className="text-base font-medium text-gray-900">Payouts</p>
                  </div>

                  <TextboxWrapper
                    register={register}
                    name="iban"
                    title="Bank account number for payouts*"
                    error={errors?.iban}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('iban')}
                  />

                  <TextboxWrapper
                    register={register}
                    name="iban_repeated"
                    title="Repeat bank account number*"
                    error={errors?.iban_repeated}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('iban_repeated')}
                  />

                  <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />
                  <div className="col-span-6">
                    <p className="text-base font-medium text-gray-900">Contacts</p>
                  </div>

                  <TextboxWrapper
                    register={register}
                    name="email"
                    title="Email for invoices*"
                    error={errors?.email}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('email')}
                  />

                  <TextboxWrapper
                    register={register}
                    name="phone"
                    title="Contact phone*"
                    error={errors?.phone}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                    onChange={() => clearErrors('phone')}
                  />

                </div>

                <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />
                <div className="col-span-6">
                  <p className="text-base font-medium text-gray-900">Terms of the contract</p>
                </div>

                <pre className="w-full whitespace-pre-wrap font-serif max-h-[400px] overflow-y-scroll px-4 py-4 border border-gray-200">
                  {itemData.terms_and_conditions}
                </pre>

                {!!itemData.general_terms_link && (
                <div className="text-base font-medium text-gray-900">
                  To read general terms & conditions{' '}
                  <a target="_blank" className="text-indigo-600 hover:text-indigo-900" href={itemData.general_terms_link} rel="noreferrer">
                    click here
                  </a>.
                </div>
                )}

                <CheckboxWrapper
                  name="terms_accepted"
                  title="I have read and agree to the terms and conditions. I hereby confirm that all the information provided by me is accurate and complete."
                  responsiveClassName="col-span-6 lg:col-span-4"
                  checked={termsAccepted}
                  onChange={() => {
                    setTermsAccepted((prev) => !prev);
                  }}
                />

              </div>

              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white
                   bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60 disabled:hover:bg-indigo-600"
                  disabled={!termsAccepted || isLoading}
                >
                  Confirm
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
